import React from 'react';
import { AlertOctagon, AlertTriangle, AlertCircle, Shield, Key, FileWarning } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: string | number;
  percentage: number;
  color: string;
  icon: React.ElementType;
  onClick?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, percentage, color, icon: Icon, onClick }) => (
  <div className="bg-[#0F1629]/50 rounded-xl p-4 backdrop-blur-sm border border-gray-800/50 cursor-pointer" onClick={onClick}>
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-400">{title}</span>
        <div className={`p-1.5 rounded-lg ${color}/10`}>
          <Icon className={`h-4 w-4 ${color}`} />
        </div>
      </div>
      <div className={`text-3xl font-bold ${color}`}>{value}</div>
      <div className="flex items-center space-x-2">
        <div className="flex-1 h-1 rounded-full bg-gray-800">
          <div
            className={`h-full rounded-full ${color}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-xs text-gray-500">{percentage}%</span>
      </div>
    </div>
  </div>
);

interface FindingsMetricsProps {
  metrics: {
    severity_counts: {
      critical: number;
      high: number;
      medium: number;
      low: number;
      info: number;
    };
    credentials_stats: {
      public_leaks: number;
      stealer_leaks: number;
      total_leaks: number;
    };
    total_dataleaks: number;
  } | null;
  handleSeverityChange?: (severity: string) => void;
}

export const FindingsMetrics: React.FC<FindingsMetricsProps> = ({ metrics, handleSeverityChange }) => {
  const calculatePercentage = (value: number) => 
    ((value / (metrics?.credentials_stats?.total_leaks || 0)) * 100).toFixed(1);

  const metricCards = [
    {
      title: 'Critical'.toUpperCase(),
      value: metrics?.severity_counts?.critical || 0,
      percentage: Number(calculatePercentage(metrics?.severity_counts?.critical || 0)),
      color: 'text-red-500',
      icon: AlertOctagon,
      ...(handleSeverityChange && {onClick: () => {
        handleSeverityChange('critical');
      }})
    },
    {
      title: 'High'.toUpperCase(),
      value: metrics?.severity_counts?.high || 0,
      percentage: Number(calculatePercentage(metrics?.severity_counts?.high || 0)),
      color: 'text-violet-500',
      icon: AlertTriangle,
      ...(handleSeverityChange && {onClick: () => {
        handleSeverityChange('high');
      }})
    },
    {
      title: 'Medium'.toUpperCase(),
      value: metrics?.severity_counts?.medium || 0,
      percentage: Number(calculatePercentage(metrics?.severity_counts?.medium || 0)),
      color: 'text-yellow-500',
      icon: AlertCircle,
      ...(handleSeverityChange && {onClick: () => {
        handleSeverityChange('medium');
      }})
    },
    {
      title: 'Low'.toUpperCase(),
      value: metrics?.severity_counts?.low || 0,
      percentage: Number(calculatePercentage(metrics?.severity_counts?.low || 0)),
      color: 'text-emerald-500',
      icon: Shield,
      ...(handleSeverityChange && {onClick: () => {
        handleSeverityChange('low');
      }})
    },
    // {
    //   title: 'Credentials'.toUpperCase(),
    //   value: metrics?.credentials_stats?.total_leaks || 0,
    //   percentage: Number(calculatePercentage(metrics?.credentials_stats?.total_leaks || 0)),
    //   color: 'text-blue-500',
    //   icon: Key,
    //   onClick: () => {
    //     window.location.href = '/data-protection/stealer-leaks';
    //   }
    // },
    // {
    //   title: 'Sensitive'.toUpperCase(),
    //   value: metrics?.total_dataleaks || 0,
    //   percentage: Number(calculatePercentage(metrics?.total_dataleaks || 0)),
    //   color: 'text-purple-500',
    //   icon: FileWarning,
    //   onClick: () => {
    //     window.location.href = '/data-protection/public-leaks';
    //   }
    // }
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {metricCards.map((card, index) => (
        <MetricCard key={index} {...card} />
      ))}
    </div>
  );
};